import { PrivateRoutes } from '@/constants/routes'
import { webApiService } from '@/services'
import { parseDate } from '@internationalized/date'
import { Button } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { MdArrowBackIos } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import FormStep1 from './components/FormStep1'
import FormStep2 from './components/FormStep2'
import StepButtons from './components/StepButtons'
import Stepper from './components/Stepper'
import { useFormCampaignContext } from './hooks/useFormCampaignContext'
import { FormCampaign } from '@/models/FormCampaignProps'
import { initialStateFormCampaignContext } from '@/constants/InitialState'

const ContributionFormPage = () => {
  const { id, slug } = useParams()
  const navigate = useNavigate()
  const { ctx, saveCtx } = useFormCampaignContext()
  const prevBrandIdRef = useRef<string | null>(null)
  // Lógica trasladada de FormStep1
  const handleChange = (key: string, value: any) => {
    console.log('value ==> ', value)
    console.log('key ==> ', key)
    saveCtx({ ...ctx, form: { ...ctx.form, [key]: value }, initialLoad: false })
  }

  useEffect(() => {
    console.log('ctx.form.scope ==> ', ctx.form.scope)
    // Solo limpiar si NO es carga inicial
    if (ctx.initialLoad) return // 👈 esto nunca falla

    if (ctx.form.scope === 'BUY') {
      webApiService.getAllBuy().then((res) => {
        saveCtx({ ...ctx, brandList: [], modelList: [], buyList: res.data, form: { ...ctx.form, brandId: '', modelId: '', sbuId: '' } })
      })
    } else {
      saveCtx({ ...ctx, brandList: [], modelList: [], buyList: [], form: { ...ctx.form, brandId: '', modelId: '', sbuId: '' } })
    }
    console.log('entro en ctx.form.scope ==> ', ctx.initialLoad)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.form.scope])

  useEffect(() => {
    if (ctx.initialLoad) return // 👈 esto nunca falla
    if ((ctx.form.scope === 'BRAND' || ctx.form.scope === 'MODEL') && ctx.form.sbuId !== '') {
      console.log('entro en ctx.form.sbuId ==> ', ctx.initialLoad)
      saveCtx({ ...ctx, loading: true, brandList: [], modelList: [], form: { ...ctx.form, brandId: '', modelId: '' } })
      webApiService.getAllBrandBySbu(String(ctx.form.sbuId), String(slug)).then((res) => {
        saveCtx({ ...ctx, loading: false, brandList: res.data, modelList: [], form: { ...ctx.form, brandId: '', modelId: '' } })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.form.sbuId])

  useEffect(() => {
    if (ctx.initialLoad) return

    const brandId = ctx.form.brandId
    if (ctx.form.scope === 'MODEL' && brandId !== '') {
      if (prevBrandIdRef.current === brandId) {
        console.log('⏭️ Saltando getAllModelByBrand, brandId no cambió:', brandId)
        return
      }

      prevBrandIdRef.current = brandId // ✅ se guarda el último valor real
      console.log('⚡ Ejecutando getAllModelByBrand con brandId:', brandId)

      saveCtx({
        ...ctx,
        loading: true,
        modelList: [],
        form: { ...ctx.form, modelId: '' }
      })

      webApiService
        .getAllModelByBrand(String(ctx.form.sbuId), String(brandId), String(slug))
        .then((res) => {
          saveCtx({
            ...ctx,
            loading: false,
            modelList: res.data,
            form: { ...ctx.form, modelId: '' }
          })
        })
    }
  }, [ctx.form.brandId])

  const loadData = async () => {
    saveCtx({ ...ctx, loading: true, sbuList: [] })

    const [contribution, sbu] = await Promise.all([
      webApiService.getContribution(id === 'new' ? 0 : Number(id), String(slug)),
      webApiService.getAllSbu(String(slug))
    ])

    let objCampaign: FormCampaign = initialStateFormCampaignContext.form

    if (id !== 'new') {
      objCampaign = {
        id: contribution.data.id,
        name: contribution.data.name,
        scope: contribution.data.scope,
        sbuId: String(contribution.data.id_sbu),
        brandId: String(contribution.data.id_brand),
        modelId: String(contribution.data.id_model),
        buy: String(contribution.data.id_buy),
        startDate: parseDate(new Date(contribution.data.start_date).toISOString().split('T')[0]),
        endDate: contribution.data.end_date === null ? parseDate(new Date().toISOString().split('T')[0]) : parseDate(new Date(contribution.data.end_date).toISOString().split('T')[0]),
        isInfinite: contribution.data.is_infinite === 1,
        maxQuantity: contribution.data.max_quantity,
        amount: contribution.data.amount,
        active: contribution.data.active === 1
      }
    }

    const arrPromises = []
    if (objCampaign.scope === 'BUY') {
      arrPromises.push(webApiService.getAllBuy())
    }
    if (objCampaign.scope === 'BRAND') {
      arrPromises.push(webApiService.getAllBrandBySbu(objCampaign.sbuId, String(slug)))
    }
    if (objCampaign.scope === 'MODEL') {
      arrPromises.push(webApiService.getAllBrandBySbu(objCampaign.sbuId, String(slug)))
      arrPromises.push(webApiService.getAllModelByBrand(objCampaign.sbuId, objCampaign.brandId, String(slug)))
    }

    const responses = arrPromises.length ? await Promise.all(arrPromises) : []

    saveCtx({
      ...ctx,
      loading: false,
      form: objCampaign,
      sbuList: sbu.data,
      brandList: responses[0]?.data ?? [],
      modelList: responses[1]?.data ?? [],
      buyList: responses[0]?.data ?? []
    })
  }

  useEffect(() => {
    loadData()
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }} className='space-y-4'
    >
      <div className='grid grid-cols-2'>
        <div className='flex items-center'>
          <Button
            variant='ghost' size='sm' color='primary' onClick={() => navigate(PrivateRoutes.CONTRIBUTIONS.replace(':slug', String(slug)))} startContent={
              <MdArrowBackIos className='text-black/50 mb-0.5 dark:text-white/90 text-slate-400 pointer-events-none flex-shrink-0' />
        }
          >Volver
          </Button>
          <h1 className='font-black md:text-2xl ml-2'>{id === 'new' ? 'Nueva campaña' : 'Editar campaña'}</h1>
        </div>
      </div>
      <div className='bg-white rounded-2xl shadow-md p-2 mt-4 py-8'>
        <Stepper />
        <FormStep1
          className={ctx.step > 0 ? 'hidden' : ''}
          onChange={handleChange}
        />
        <FormStep2 className={ctx.step !== 1 ? 'hidden' : ''} onChange={handleChange} />
        <StepButtons />
      </div>
    </motion.div>
  )
}

export default ContributionFormPage
